import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Subscription } from 'rxjs';
import {
    AGURenderService,
    AGUSortOption,
    AGUToastService,
    AGUViewportService,
    AGUAppLocation,
    AGUAppModuleAccent,
    AGUTabStripTab,
    AGUSearchAttachment,
    AGUResultCount,
    FiltersFromEcho,
    FinderPageState,
    FinderTab,
    RevealDirection,
    InitialFinderState,
    PageResultCount,
    ResetFilters,
    StripQueryGroups,
    WithPage,
    WithQuery,
    FinderTabScrollMemory,
    OriginOf,
    AGUAdPlacement,
    AGUAdSessionService,
} from 'agu_app_shell';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { RaCompositionState } from '../../ra-composition.state';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { ResearchApiService } from '../../services/research-api.service';
import { MemberItemStateChange, MemberItemStateService } from '../../services/member-item-state.service';
import {
    ItemKind,
    PersonSort,
    ResearchJobPreferenceInput,
    ResearchPeopleFacets,
    ResearchPeopleFilterInput,
    ResearchOverlap,
    ResearchPersonMatch,
    ResearchQueryInput,
    UnderstoodPeopleConstraint,
} from '../../models/research.models';
import {
    BuildPeopleFilterPills,
    CountActivePeopleFilterGroups,
    PEOPLE_QUERY_GROUP_FIELDS,
    PeoplePillGroupOf,
    RemovePeopleFilterGroup,
} from './people-filters.component';
import { ActiveFilterPill } from '../../finder/active-filter-pills';
import { ActiveGroupsFrom, PhoneFinderChrome, ToSortOptions } from '../../finder/phone-finder-chrome';
import { DescribeError } from '../shared/describe-error';
import { LoadFailures } from '../shared/load-failures';
import { BrowseAllLabel, FinderAllEmptyCopy, FinderRecommendedEmptyCopy, LISTING_GONE_COPY, ListErrorCopy, PEOPLE_LOCK_COPY, STATE_NOUN, StatePanelCopy } from '../shared/state-copy';
import { PortalConfigService } from '../../services/portal-config.service';
import { MembershipService } from '../../services/membership.service';
import { DismissedSlots } from '../shared/dismissed-slots';
import { PinnedFirst } from '../shared/pinned-first';
import { NextResultsPanelId } from '../shared/panel-id';
import { RECOMMENDED_COUNT } from '../shared/recommended-count';
import { AssertRecommendationsRefreshed } from '../shared/recommendations-refreshed';

/** Views this module has. Narrows AGUAppLocation for every internal comparison. `'profile'` renders `ra-person-detail` (the detail-view chrome, CSV 2026-10-24). */
export type PeopleFinderView = 'search' | 'profile';

const PERSON_KIND: ItemKind = 'person';
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Verbatim from plans/research-accelerator/mockups/Collaborators.txt's "Try" row (also present as `trypill` buttons in the bundled HTML mockup). */
const TRY_CHIPS: readonly string[] = ['Remote sensing co-PI', 'Gulf Coast hydrologists', 'Ocean–atmosphere modelers'];

/** Tab order for the reveal direction: All then Recommended. */
const TAB_ORDER: ReadonlyArray<FinderTab> = ['all', 'recommended'];

const SORT_OPTIONS: ReadonlyArray<{ key: PersonSort; label: string }> = [
    { key: 'best', label: 'Best match' },
    { key: 'mostRecentWork', label: 'Most recent work' },
    { key: 'mostMutual', label: 'Most mutual connections' },
    { key: 'nameAZ', label: 'Name A-Z' },
];

/** "No constraint" shape for every ResearchPeopleFilterInput field — arrays → [], booleans → false, scalars → undefined. */
export const EMPTY_PEOPLE_FILTERS: ResearchPeopleFilterInput = {
    city: [],
    country: [],
    degreeLevelMin: undefined,
    hasActiveFunding: false,
    hasFunding: false,
    hasMutualCoauthors: false,
    hasPIFunding: false,
    location: [],
    mapsMember: false,
    organization: [],
    recommendedOnly: false,
    researchArea: [],
    state: [],
    yearsExperienceMax: undefined,
    yearsExperienceMin: undefined,
};

/**
 * `ResetFilters` is generic over `Record<string, unknown>` — `ResearchPeopleFilterInput`
 * is structurally compatible (every field is an array, boolean, or optional scalar)
 * but, as an `interface` with no index signature, isn't assignable to that constraint
 * without a cast. Same justification as `ResetGrantFilters` (grant-finder-page.component.ts):
 * `ResetFilters` only reads `Object.keys`/`typeof` per value.
 */
function ResetPeopleFilters(filters: ResearchPeopleFilterInput): ResearchPeopleFilterInput {
    return ResetFilters(filters as unknown as Record<string, unknown>) as unknown as ResearchPeopleFilterInput;
}

/**
 * Collaborator Finder — member-facing expert and collaborator discovery over
 * the AGU contributor corpus (`Recommendation.Contributor`). Matching is
 * vector similarity against the member's typed query and/or attached
 * documents, served by `ResearchApiService.SearchPeople`; results carry
 * "What you share" overlaps (co-authorship, mutual co-authors, shared
 * affiliation, co-convened sessions) computed against the signed-in member's
 * own contributor record.
 *
 * Root component of the `people` module — rendered via `ngComponentOutlet` by
 * `RaSurfaceComponent` with no inputs, so every dependency it needs
 * (`ExplorerRaHostAdapter`, `ResearchApiService`, `MemberItemStateService`)
 * comes through constructor injection from the surface's providers. Structure
 * mirrors `GrantFinderPageComponent` (Task 16) field-for-field; see that
 * file's doc comments for anything not re-explained here. *
 * Product states (PD §5): skeletons while a list loads, the error panel in place of a list that failed, and the empty panels from
 * `state-copy.ts`. Truthfulness edits here: the all-empty panel drops "Save as an alert" (P/proto-states.js:97) and the
 * recommended-empty panel drops "and we will email you the moment one matches" (:98) — the hub has no alerts or match emails.
 */
/**
 * The phone quick chips' groups: the first four of this page's rail group labels, in rail order, without the `Show` toggles
 * (`chips()`, P/Member_Hub.html:918-920; labels as people-filters.component.html writes them).
 */
export const PEOPLE_PHONE_FILTER_GROUPS: readonly string[] = ['Organization', 'Location', 'Research area'];

/** Which rail group (by its label) each active-filter pill key belongs to, for the phone chips' `.on` state (P/Member_Hub.html:939-945). */
export const PEOPLE_PILL_GROUP: Readonly<Record<string, string>> = {
    organization: 'Organization',
    location: 'Location',
    country: 'Location',
    state: 'Location',
    city: 'Location',
    researchArea: 'Research area',
};

@Component({
    standalone: false,
    selector: 'ra-people-finder',
    templateUrl: './people-finder-page.component.html',
    styleUrl: './people-finder-page.component.scss',
    // data-accent turns this module's colour into the --accent cascade every
    // shared component below reads (the token layer's ra-module-accent block).
    // ra-fullwidth drops the centred 1200px reading column: EVERY mockup screen
    // is full-bleed inside `.main` (`padding:30px 32px 36px`, no max-width
    // anywhere), the sub-views included — `detail.html:29` sets
    // `.shell { max-width:none }` and caps only the inner `.detail.wide` block
    // at 900px, left-aligned. That inner measure lives on the detail view's own
    // :host (modules/shared/_detail-view.scss), so the host class is
    // unconditional here (review I-2).
    host: {
        '[attr.data-accent]': 'Accent',
        class: 'ra-fullwidth',
    },
})
export class PeopleFinderPageComponent implements OnInit, OnDestroy {
    /** The results region this page's tab strip drives, unique per instance so two RA tabs never collide on `aria-controls` (review M-9). */
    readonly ResultsPanelId = NextResultsPanelId('people');

    readonly TryChips = TRY_CHIPS;

    /** This module's colour, read off its own module definition (never restated here). */
    get Accent(): AGUAppModuleAccent {
        return this.composition.AccentOf(RA_MODULE_KEYS.People);
    }

    readonly SortOptions = SORT_OPTIONS;
    /** The sorts for the phone chips' select (P/Member_Hub.html:926-932). */
    readonly PhoneSortOptions: readonly AGUSortOption[] = ToSortOptions(SORT_OPTIONS);
    /** The rail groups the phone chips open the sheet at. */
    readonly FilterGroups = PEOPLE_PHONE_FILTER_GROUPS;
    /** Phone layout and the filter sheet's open state (P/Member_Hub.html:603-620, 915-951). Assigned in the constructor body. */
    readonly Phone: PhoneFinderChrome;
    /** Bumped when a result set lands or the tab switches; cards rise on it (P/Member_Hub.html:1807-1845). */
    RiseStamp = 0;
    /** Bumped on a tab switch only; the results section slides by RevealDirectionValue (P/grant-finder.html:1219-1232). */
    RevealStamp = 0;
    RevealDirectionValue = 0;

    state: FinderPageState<ResearchPeopleFilterInput> = InitialFinderState(EMPTY_PEOPLE_FILTERS, 'best', 12);
    view: PeopleFinderView = 'search';
    recordID: string | null = null;

    items: ResearchPersonMatch[] = [];
    total = 0;
    queryMode = 'none';

    recommendedItems: ResearchPersonMatch[] = [];
    recommendedMode = 'none';
    private recommendedLoaded = false;
    /** Recommended tab: co-authors of the member are hidden unless the member switches this off. */
    hideCoauthors = true;
    /** Id of the newest LoadRecommended call; an older call that finishes after a newer one started drops its result. */
    private recommendedRequestSeq = 0;
    /** Where each dismissed card sat, so Undo restores it in place (J-3). */
    private readonly dismissedSlots = new DismissedSlots<ResearchPersonMatch>((i) => i.contributorID);
    /** Every collaborator the member has pinned (`researchPinnedPeople`), loaded with the recommendations. */
    private pinnedPeople: ResearchPersonMatch[] = [];
    /** In flight: SetTab's URL sync comes back through OnLocationChanged -> SetTab before the first load lands (one click sent ResearchRecommendations twice). */
    private recommendedLoading = false;

    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();

    /** The product-state copy for this finder (P/proto-states.js:97-98, 115, 121). */
    readonly AllEmptyCopy: StatePanelCopy = FinderAllEmptyCopy(STATE_NOUN.People);
    readonly RecommendedEmptyCopy: StatePanelCopy = FinderRecommendedEmptyCopy(STATE_NOUN.People);
    readonly ErrorCopy: StatePanelCopy = ListErrorCopy(STATE_NOUN.People);
    readonly GoneCopy: StatePanelCopy = LISTING_GONE_COPY;
    /** The lapsed-membership lock that replaces the finder body (P/proto-states.js:135-137). */
    readonly LockCopy: StatePanelCopy = PEOPLE_LOCK_COPY;
    /** True while the token says the membership has lapsed; `unknown` (today's token) is not lapsed. */
    get Lapsed(): boolean { return this.membership?.IsLapsed ?? false; }
    /** Both lock buttons go to the AGU account page. */
    OnRenew(): void { void this.membership?.OpenRenewal(this.portalConfig); }
    readonly BrowseAll = BrowseAllLabel(STATE_NOUN.People);

    facets: ResearchPeopleFacets | null = null;

    attachment: AGUSearchAttachment | null = null;
    private documentID: string | null = null;

    showSavedOnly = false;

    /** The topic text the last understood search actually embedded, or null when none is standing. */
    private understoodText: string | null = null;
    /** Alternates the last understood search embedded — re-sent on follow-ups so every page reads one cached ranking. */
    private understoodAlternates: string[] | null = null;
    /** Named person recognized by the last understood search — re-sent on follow-ups so the named person stays pinned. */
    private understoodNamedPerson: string | null = null;
    /** Understood constraints from the last search echo. */
    private constraints: UnderstoodPeopleConstraint[] = [];

    private QuerySourceForServer(understand: boolean): ResearchQueryInput {
        const text = understand || this.understoodText === null ? this.state.query : this.understoodText;
        return { text, documentIDs: this.state.documentIDs, alternates: understand ? null : this.understoodAlternates };
    }

    private PreferencesForServer(understand: boolean): ResearchJobPreferenceInput[] | undefined {
        if (understand) return undefined;
        const prefs = this.constraints
            .filter((c) => c.stance === 'preference')
            .map((c) => ({ kind: c.kind, values: c.values, weight: c.weight }));
        return prefs.length > 0 ? prefs : undefined;
    }

    private locationSub: Subscription | null = null;
    private stateSub: Subscription | null = null;
    /**
     * Whether a search has actually run on the All tab. False on a freshly
     * opened module, which is what `IsPreSearch` renders — the page used to fire
     * an EMPTY-query search on open and render "No collaborators match your filters."
     * over the result, which reads as a verdict on a search the member never
     * made (live finding #9). Nothing is requested until they ask for something.
     */
    private searchRan = false;
    /** Per-tab scroll offsets (CSV 2026-10-24, Grant Finder row 1). Assigned in the constructor body: a field initializer would read `this.host` before TS assigns constructor-parameter properties (see `LastDismissed$`). */
    private readonly tabScroll: FinderTabScrollMemory;

    constructor(
        private readonly api: ResearchApiService,
        private readonly itemState: MemberItemStateService,
        private readonly host: ExplorerRaHostAdapter,
        private readonly composition: RaCompositionState,
        private readonly render: AGURenderService,
        @Optional() @Inject(AGUViewportService) viewport: AGUViewportService | null = null,
        /** The phone shell's toast, surface-provided (P/Member_Hub.html:953-958); absent outside a surface. */
        @Optional() @Inject(AGUToastService) private readonly toast: AGUToastService | null = null,
        /** The Featured placements' session state, surface-provided (PD §3); absent outside a surface. */
        @Optional() @Inject(AGUAdSessionService) private readonly ads: AGUAdSessionService | null = null,
        /** The memoised portal config, for the error panel's Contact support address (PD §5); absent outside a surface. */
        @Optional() @Inject(PortalConfigService) private readonly portalConfig: PortalConfigService | null = null,
        /** The surface's membership state (token claims); absent outside a surface, where nothing is locked. */
        @Optional() @Inject(MembershipService) private readonly membership: MembershipService | null = null
    ) {
        this.Phone = new PhoneFinderChrome(viewport, () => this.render.Notify());
        this.tabScroll = new FinderTabScrollMemory(host);
    }

    ngOnInit(): void {
        this.Phone.Start();
        this.locationSub = this.host.Location$.subscribe((loc) => this.OnLocationChanged(loc));
        this.stateSub = this.itemState.Changes$.subscribe((change) => this.OnItemStateChanged(change));
        void this.LoadFacets();
        void this.failures.ReadSupport(this.portalConfig);
        // The Recommended pill carries its count from first paint, as in the mockup (grants.html:446,
        // P-G4/P-C3/P-J3): load in the background; the tab shows progress/errors only while it is open.
        if (!this.recommendedLoaded && !this.recommendedLoading) void this.LoadRecommended();
    }

    ngOnDestroy(): void {
        this.locationSub?.unsubscribe();
        this.stateSub?.unsubscribe();
        this.Phone.Stop();
    }

    /** A getter, not a field initializer — see GrantFinderPageComponent.LastDismissed$'s doc comment for why. */
    get LastDismissed$() {
        return this.itemState.LastDismissed$;
    }

    // ── Location / navigation ───────────────────────────────────────────

    private OnLocationChanged(loc: AGUAppLocation): void {
        if (loc.ModuleKey !== RA_MODULE_KEYS.People) return;

        if (loc.View === 'profile') {
            this.view = 'profile';
            this.recordID = loc.RecordID ?? null;
            return;
        }

        this.view = 'search';
        this.recordID = null;

        if (loc.RecordID === 'recommended') {
            this.SetTab('recommended');
        }
    }

    /**
     * Leaves the profile view for where it was opened from (`From`), else this finder's search view — the crumb's first ancestor
     * (PD §6). The "no profile specified" state's way out; the loaded view leaves through its own crumb. Never `host.Back()`: the
     * crumb names a place, and the browser's Back still walks history through the URL-synced adapter.
     */
    LeaveDetail(): void {
        this.host.NavigateTo(OriginOf(this.host.CurrentLocation, 'search'));
    }

    /** The gone-listing panel's "See similar" (P/proto-states.js:121): this finder's All tab. */
    OnSeeSimilar(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.People, View: 'search' });
        this.SetTab('all');
    }

    /** The gone-listing panel's "Back to Home" (P/proto-states.js:121). */
    OnBackToHome(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Dashboard, View: 'home' });
    }

    /** "Widen research areas" (P/proto-states.js:97-98): the member's profile, where the research areas show. */
    OnWidenResearchAreas(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Profile, View: 'me' });
    }

    OnOpen(item: ResearchPersonMatch): void {
        // The finder place on screen (its tab) is the crumb's origin; the module stays mounted, so its query and filters survive.
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.People, View: 'profile', RecordID: item.contributorID, From: this.host.CurrentLocation });
    }

    /**
     * The detail view's "Recommended" chip: only for a person opened from the Recommended tab who is
     * among this member's recommendations — the same rule the cards follow (QA 2026-10-05, grants).
     */
    get DetailIsRecommended(): boolean {
        return this.state.tab === 'recommended' && this.recommendedItems.some((i) => i.contributorID === this.recordID);
    }

    /**
     * The opened card's overlaps for the detail's "What you share" (CSV 2026-10-24). The profile
     * query carries none, so the match in memory supplies them; a deep link with no match gets [].
     */
    get DetailOverlaps(): readonly ResearchOverlap[] {
        return this.DetailMatch?.overlaps ?? [];
    }

    /** The opening card's `isNew` for the detail's pill; false on a deep link with no match in memory. */
    get DetailIsNew(): boolean {
        return this.DetailMatch?.isNew ?? false;
    }

    /** The match in memory for the open record, from either list (the detail's overlaps and New pill come from it). */
    private get DetailMatch(): ResearchPersonMatch | null {
        const id = this.recordID;
        if (id === null) return null;
        return this.items.find((i) => i.contributorID === id) ?? this.recommendedItems.find((i) => i.contributorID === id) ?? null;
    }

    // ── Tabs ─────────────────────────────────────────────────────────────

    get Tabs(): AGUTabStripTab[] {
        return [
            { Key: 'all', Label: 'All people', Count: this.total },
            // Finding #13 (Task 15 round 1): `recommendedItems` is empty until
            // `LoadRecommended()` runs on the first switch TO this tab, so binding the
            // badge straight to its length made a member with recommendations waiting
            // read `Recommended 0` — while Home, in the same session, said
            // `See 12 recommended` for the same items. `null` renders NO badge
            // (`AGUTabStripTab.Count`: "Absent or null renders no badge — 0 is a real,
            // renderable count"), which is the honest state before the load; after it,
            // a genuine 0 still shows as 0. Home's own `{N}` is the requested page size
            // (`research.recommend.count`), not a count of this member's rows, so
            // seeding the badge from it would render a fabricated number.
            { Key: 'recommended', Label: 'Recommended', Count: this.recommendedLoaded ? this.recommendedItems.length : null },
        ];
    }

    OnTabChange(key: string): void {
        if (key === 'all' || key === 'recommended') this.SetTab(key);
    }

    SetTab(tab: FinderTab): void {
        if (this.state.tab !== tab) {
            this.tabScroll.Switch(this.state.tab, tab);
            // P/grant-finder.html:1219-1232: the incoming section slides in from the side its tab sits on.
            this.RevealDirectionValue = RevealDirection(TAB_ORDER.indexOf(this.state.tab), TAB_ORDER.indexOf(tab));
            this.RevealStamp++; // the cards are re-created on the switch and rise on init through the reveal's first-8 pass
            this.state = { ...this.state, tab };
        }
        if (tab === 'recommended' && !this.recommendedLoaded) {
            if (this.recommendedLoading) this.loading = true;
            else void this.LoadRecommended();
        }
        this.SyncTabToLocation();
    }

    /**
     * Keeps the address bar's `id=recommended` in step with the tab on screen (D-20/G-15).
     * A tab switch and a fresh query (`WithQuery` lands on All) used to change state only, so
     * the URL kept a stale `id=recommended` and browser Back re-applied the Recommended tab
     * the member had already left. `ResetTo` replaces the entry rather than pushing one: a
     * tab is a view of the same place, not somewhere Back should walk through. Its own
     * `Location$` emission comes back through `OnLocationChanged` with the URL already
     * agreeing with the tab, so the loop ends there.
     */
    private SyncTabToLocation(): void {
        const loc = this.host.CurrentLocation;
        if (!loc || loc.ModuleKey !== RA_MODULE_KEYS.People || loc.View !== 'search') return;
        const onRecommended = this.state.tab === 'recommended';
        if ((loc.RecordID === 'recommended') === onRecommended) return;
        this.host.ResetTo({ ModuleKey: RA_MODULE_KEYS.People, View: 'search', RecordID: onRecommended ? 'recommended' : undefined });
    }

    // ── Search / recommendations ────────────────────────────────────────

    /**
     * Whether a follow-up has anything left to ask the server: a topic, a document, a standing
     * filter, or a recognized named person. A constraint-only query echoes an empty topic, so
     * removing its last pill would otherwise send `text ''` with no filters and render an empty
     * results state with nothing standing (G-3).
     */
    private HasSomethingToSearch(): boolean {
        const source = this.QuerySourceForServer(false);
        return (
            (source.text ?? '').trim().length > 0 ||
            (source.documentIDs?.length ?? 0) > 0 ||
            this.ActiveFilterCount > 0 ||
            (this.understoodNamedPerson ?? '').trim().length > 0
        );
    }

    /** Back to the module as it opened (`IsPreSearch`) — no request, no result verdict, an empty search box. */
    private ReturnToPreSearch(): void {
        this.searchRan = false;
        this.items = [];
        this.total = 0;
        this.queryMode = 'none';
        this.error = null;
        this.understoodText = null;
        this.understoodAlternates = null;
        this.understoodNamedPerson = null;
        this.constraints = [];
        this.state = { ...this.state, query: '', page: 1 };
    }

    async Search(understand = false): Promise<void> {
        if (!understand && !this.HasSomethingToSearch()) {
            this.ReturnToPreSearch();
            return;
        }
        this.searchRan = true;
        this.loading = true;
        this.error = null;
        try {
            const result = await this.api.SearchPeople({
                query: this.QuerySourceForServer(understand),
                count: this.state.pageSize,
                page: this.state.page,
                filters: this.ActiveFiltersForServer(),
                sort: this.state.sort as PersonSort,
                understand,
                namedPerson: understand ? null : this.understoodNamedPerson,
                preferences: this.PreferencesForServer(understand),
            });
            if (result.understood) {
                this.state = { ...this.state, filters: FiltersFromEcho(result.understood.filters, EMPTY_PEOPLE_FILTERS) };
                this.understoodText = result.understood.searchText;
                this.understoodAlternates = result.understood.alternates;
                this.understoodNamedPerson = result.understood.namedPerson;
                this.constraints = result.understood.constraints ?? [];
            }
            this.items = result.results;
            this.dismissedSlots.Clear();
            this.total = result.total ?? result.results.length;
            this.queryMode = result.queryMode;
            this.itemState.Prime(
                PERSON_KIND,
                this.items.map((i) => ({ key: i.contributorID, itemState: i.itemState }))
            );
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
        } finally {
            this.loading = false;
            this.RiseStamp++; // a fresh result set rises (P/Member_Hub.html:1830)
        }
    }

    /** Flips the co-author filter and reloads; a load already in flight is superseded (LoadRecommended's sequence guard). */
    async OnHideCoauthorsChange(hide: boolean): Promise<void> {
        this.hideCoauthors = hide;
        this.recommendedLoaded = false;
        await this.LoadRecommended();
        this.render.Notify(); // the switch can change after an await, outside the click: repaint a zoneless host
    }

    async LoadRecommended(): Promise<void> {
        const seq = ++this.recommendedRequestSeq;
        this.recommendedLoading = true;
        const shown = this.state.tab === 'recommended';
        if (shown) {
            this.loading = true;
            this.error = null;
        }
        try {
            const [result, pinned] = await Promise.all([
                this.api.Recommendations([PERSON_KIND], RECOMMENDED_COUNT, !this.hideCoauthors),
                this.api.PinnedPeople ? this.api.PinnedPeople() : Promise.resolve([]),
            ]);
            if (seq !== this.recommendedRequestSeq) return; // a newer toggle owns the list now
            AssertRecommendationsRefreshed(result, PERSON_KIND); // a kind the server could not refresh is a failed load, not an empty list
            this.recommendedItems = result.people;
            this.pinnedPeople = pinned;
            this.itemState.Prime(
                PERSON_KIND,
                pinned.map((i) => ({ key: i.contributorID, itemState: i.itemState }))
            );
            this.recommendedMode = result.mode;
            this.recommendedLoaded = true;
            this.itemState.Prime(
                PERSON_KIND,
                this.recommendedItems.map((i) => ({ key: i.contributorID, itemState: i.itemState }))
            );
            if (this.state.tab === 'recommended') this.failures.Reset();
        } catch (e) {
            // A background load that fails stays unloaded, so opening the tab retries it.
            if (seq !== this.recommendedRequestSeq) return;
            if (this.state.tab === 'recommended') {
                this.error = DescribeError(e);
                this.failures.Record(e);
            }
        } finally {
            if (seq === this.recommendedRequestSeq) this.recommendedLoading = false;
            if (seq === this.recommendedRequestSeq && (shown || this.state.tab === 'recommended')) {
                this.loading = false;
                this.RiseStamp++; // only when the list on screen is the one that landed; a background prefetch must not replay the All tab's rise
            }
        }
    }

    Retry(): void {
        if (this.state.tab === 'recommended') void this.LoadRecommended();
        else void this.Search();
    }

    private async LoadFacets(): Promise<void> {
        try {
            this.facets = await this.api.PeopleFacets();
        } catch {
            // Facets are supplementary (dropdown/checkbox option lists) — a
            // failure degrades the filter panel to empty option lists rather
            // than blocking the page.
        }
    }

    /** Strips every default/empty field so the server never sees e.g. `organization: []`. */
    private ActiveFiltersForServer(): ResearchPeopleFilterInput {
        const f = this.state.filters;
        const out: ResearchPeopleFilterInput = {};
        if (f.organization && f.organization.length > 0) out.organization = f.organization;
        if (f.location && f.location.length > 0) out.location = f.location;
        if (f.researchArea && f.researchArea.length > 0) out.researchArea = f.researchArea;
        if (f.hasMutualCoauthors) out.hasMutualCoauthors = true;
        if (f.recommendedOnly) out.recommendedOnly = true;
        if (f.country && f.country.length > 0) out.country = f.country;
        if (f.state && f.state.length > 0) out.state = f.state;
        if (f.city && f.city.length > 0) out.city = f.city;
        if (f.degreeLevelMin !== undefined && f.degreeLevelMin !== null) out.degreeLevelMin = f.degreeLevelMin;
        if (f.yearsExperienceMin !== undefined && f.yearsExperienceMin !== null) out.yearsExperienceMin = f.yearsExperienceMin;
        if (f.yearsExperienceMax !== undefined && f.yearsExperienceMax !== null) out.yearsExperienceMax = f.yearsExperienceMax;
        if (f.hasFunding) out.hasFunding = true;
        if (f.hasPIFunding) out.hasPIFunding = true;
        if (f.hasActiveFunding) out.hasActiveFunding = true;
        if (f.mapsMember) out.mapsMember = true;
        return out;
    }

    // ── Search hero ──────────────────────────────────────────────────────

    OnQueryChange(query: string): void {
        this.understoodText = null;
        this.understoodAlternates = null;
        this.understoodNamedPerson = null;
        this.state = { ...this.state, query };
    }

    /** A fresh submit re-extracts; the previous query's groups belong to the previous text, so drop them first while preserving manual picks. */
    private SubmitFresh(query: string): void {
        const fromQuery = this.constraints.filter((c) => c.origin === 'query').map((c) => PeoplePillGroupOf(c.kind));
        const stripped = StripQueryGroups(this.state.filters, PEOPLE_QUERY_GROUP_FIELDS, fromQuery, EMPTY_PEOPLE_FILTERS);
        this.understoodText = null;
        this.understoodAlternates = null;
        this.understoodNamedPerson = null;
        this.constraints = [];
        this.state = { ...WithQuery(this.state, query), filters: stripped };
        this.tabScroll.Clear(); // a fresh result set has no "place" to return to
        this.SyncTabToLocation();
        void this.Search(true);
    }

    OnSubmitted(query: string): void {
        this.SubmitFresh(query);
    }

    OnSuggestionSelected(suggestion: string): void {
        this.SubmitFresh(suggestion);
    }

    async OnFileSelected(file: File): Promise<void> {
        if (file.size > MAX_ATTACHMENT_BYTES) {
            this.attachment = { Name: file.name, Status: 'failed', Error: 'Files must be under 10 MB' };
            return;
        }
        this.attachment = { Name: file.name, Status: 'uploading' };
        try {
            const doc = await this.api.UploadDocument(file, 'search', false);
            this.documentID = doc.documentID;
            this.attachment = { Name: doc.fileName, Status: 'ready' };
            this.state = { ...this.state, documentIDs: [doc.documentID] };
        } catch (e) {
            this.attachment = { Name: file.name, Status: 'failed', Error: e instanceof Error ? e.message : 'Upload failed' };
        }
    }

    async OnAttachmentRemoved(): Promise<void> {
        const id = this.documentID;
        this.attachment = null;
        this.documentID = null;
        this.state = { ...this.state, documentIDs: [] };
        if (id) {
            try {
                await this.api.DocumentDelete(id);
            } catch {
                // Best-effort cleanup — the UI has already dropped the attachment
                // either way, so a failed server-side delete isn't worth surfacing.
            }
        }
    }

    // ── Filters / sort / paging ──────────────────────────────────────────

    get ActiveFilterCount(): number {
        return CountActivePeopleFilterGroups(this.state.filters);
    }

    OnFiltersChange(filters: ResearchPeopleFilterInput): void {
        this.state = { ...this.state, filters, page: 1 };
        void this.Search();
    }

    /** The rail's "Suggested only" switch mirrors `filters.recommendedOnly` — a server-side filter on All collaborators (spec §8.1), not a tab switch. */
    OnRecommendedOnlyChange(on: boolean): void {
        this.state = { ...this.state, filters: { ...this.state.filters, recommendedOnly: on }, page: 1 };
        void this.Search();
    }

    /** The rail groups with at least one active filter — the phone chips render those `.on` (P/Member_Hub.html:653, 939-945). */
    get ActiveFilterGroups(): readonly string[] {
        return ActiveGroupsFrom(this.FilterPills, PEOPLE_PILL_GROUP);
    }

    get FilterPills(): ActiveFilterPill[] {
        if (this.state.tab !== 'all') return [];
        const understood = BuildPeopleFilterPills(this.state.filters, this.constraints);
        const claimed = new Set(understood.map((p) => p.Key));
        const manual = BuildPeopleFilterPills(this.state.filters).filter((p) => !claimed.has(p.Key));
        return [...understood, ...manual];
    }

    OnRemoveFilterGroup(key: string): void {
        this.constraints = this.constraints.filter((c) => PeoplePillGroupOf(c.kind) !== key);
        this.state = { ...this.state, filters: RemovePeopleFilterGroup(this.state.filters, key), page: 1 };
        void this.Search();
    }

    OnClearFilters(): void {
        this.constraints = [];
        this.understoodText = null;
        this.understoodAlternates = null;
        this.understoodNamedPerson = null;
        this.state = { ...this.state, query: '', filters: ResetPeopleFilters(this.state.filters), page: 1 };
        this.showSavedOnly = false;
        void this.Search();
    }

    OnSortChange(sort: string): void {
        this.state = { ...this.state, sort, page: 1 };
        void this.Search();
    }

    OnPageChange(page: number): void {
        this.state = WithPage(this.state, page);
        void this.Search();
    }

    // ── Display lists / empty states ────────────────────────────────────

    /**
     * Scoped to the Recommended tab only — on the All tab this must be empty
     * even if a Recommended item happens to be pinned, otherwise `IsEmpty`
     * below stays false when the All tab's own filtered results are empty
     * (empty grid + pagination showing Total 0 instead of the "No
     * collaborators match your filters" state — review finding, 2026-09-03).
     */
    get PinnedItems(): ResearchPersonMatch[] {
        if (this.state.tab !== 'recommended') return [];
        const byId = new Map<string, ResearchPersonMatch>();
        for (const i of [...this.pinnedPeople, ...this.recommendedItems]) {
            if (i.itemState.pinned && !i.itemState.dismissed && !byId.has(i.contributorID)) {
                byId.set(i.contributorID, i);
            }
        }
        return [...byId.values()];
    }

    /** Whether a card is one of the member's recommendations: a pinned person who is not keeps no Recommended chip (C-16). */
    IsRecommendation(item: ResearchPersonMatch): boolean {
        return this.recommendedItems.some((i) => i.contributorID === item.contributorID);
    }

    private get UnpinnedRecommended(): ResearchPersonMatch[] {
        return this.recommendedItems.filter((i) => !i.itemState.pinned);
    }

    /**
     * The All tab hoists pinned results to the front of the page it is showing
     * (Ruling R-F12, `PinnedFirst`) — the Recommended tab lifts them into its
     * own "Pinned by you" section instead, so it must not reorder as well.
     */
    get DisplayItems(): ResearchPersonMatch[] {
        const source = this.state.tab === 'recommended' ? this.UnpinnedRecommended : PinnedFirst(this.items);
        return source.filter((i) => !this.showSavedOnly || i.itemState.saved);
    }

    /**
     * The All tab before the member has asked for anything. Distinct from
     * "no results": nothing has been searched, so there is nothing to report an
     * absence of, and offering "Clear all filters" for filters they never set
     * only confuses (live finding #9).
     */
    /**
     * The landing strip under the search hero (P/Member_Hub.html:1383-1401): shown while the finder is in its landing state —
     * Recommended, or All before any search — and gone once results show, as the prototype hides it while `#searchSec` is visible.
     */
    get HeroStrip(): AGUAdPlacement | null {
        const landing = this.state.tab === 'recommended' || this.IsPreSearch;
        return landing ? (this.ads?.First('people', 'strip') ?? null) : null;
    }

    /** The Circles row after the recommended grid or the results (P/Member_Hub.html:1586-1610). */
    get Circles(): AGUAdPlacement | null {
        return this.ads?.First('people', 'circles') ?? null;
    }

    /** The line inside the no-results panel (P/Member_Hub.html:1611-1612). */
    get NoResultsLine(): AGUAdPlacement | null {
        return this.ads?.First('people', 'no-results-line') ?? null;
    }

    get IsPreSearch(): boolean {
        return this.state.tab === 'all' && !this.searchRan;
    }

    /**
     * The toolbar's count line (grants.html:960) — All tab only, and only once a
     * search has actually run.
     *
     * The on-page count is `DisplayItems.length`, what the column actually
     * renders, NOT `items.length`, the unfiltered page: the `Saved only` switch
     * filters the page client-side, so the raw page count made the line
     * contradict the list in front of the member (final review, finding #1).
     * `Total` stays the corpus total.
     */
    get ResultCount(): AGUResultCount | null {
        // A failed load shows no count (P/proto-states.js:117, `#count` '').
        if (this.state.tab !== 'all' || this.IsPreSearch || this.error) return null;
        return PageResultCount(this.total, this.state.page, this.state.pageSize, this.DisplayItems.length, 'person');
    }

    get ShowNothingToMatchYet(): boolean {
        return this.state.tab === 'recommended' && this.recommendedMode === 'none' && this.recommendedItems.length === 0;
    }

    /** Spec 2026-09-30 F1: the intake judged the typed text unsearchable. */
    get IsUnrecognized(): boolean {
        return this.state.tab === 'all' && this.queryMode === 'unrecognized';
    }

    get IsEmpty(): boolean {
        return this.DisplayItems.length === 0 && this.PinnedItems.length === 0;
    }

    // ── Card actions ─────────────────────────────────────────────────────

    async OnSave(item: ResearchPersonMatch, saved: boolean): Promise<void> {
        await this.itemState.Toggle(PERSON_KIND, item.contributorID, { saved });
        this.toast?.ShowSaved(saved); // The bookmark toast (P/Member_Hub.html:953-966), once the save has stuck: a failed Toggle throws.
    }

    async OnPin(item: ResearchPersonMatch, pinned: boolean): Promise<void> {
        await this.itemState.Toggle(PERSON_KIND, item.contributorID, { pinned });
    }

    async OnDismiss(item: ResearchPersonMatch): Promise<void> {
        await this.itemState.Dismiss(PERSON_KIND, item.contributorID, item.name);
        const left = this.dismissedSlots.Remove(item, this.items, this.recommendedItems);
        this.items = left.Items;
        this.recommendedItems = left.Recommended;
    }

    async Undo(): Promise<void> {
        await this.itemState.Undo();
    }

    private OnItemStateChanged(change: MemberItemStateChange): void {
        if (change.kind !== PERSON_KIND) return;
        const patch = (list: ResearchPersonMatch[]): void => {
            const item = list.find((i) => i.contributorID === change.key);
            if (item) item.itemState = change.itemState;
        };
        patch(this.items);
        patch(this.recommendedItems);
        patch(this.pinnedPeople);
        // Pinned from the All tab: carry it into the Recommended tab's `Pinned by you` section.
        if (change.itemState.pinned && !this.pinnedPeople.some((i) => i.contributorID === change.key)) {
            const source = this.items.find((i) => i.contributorID === change.key) ?? this.recommendedItems.find((i) => i.contributorID === change.key);
            if (source) this.pinnedPeople = [{ ...source, itemState: change.itemState }, ...this.pinnedPeople];
        }
        // Pinning repartitions the All tab (`DisplayItems` → `PinnedFirst`), and
        // the server-truth emission arrives after an await, outside the click
        // that started it — in a zoneless host nothing else would repaint it.
        this.render.Notify();

        // `confirmed` only, deliberately: every toggle emits twice (optimistic,
        // then server truth), and reloading on both fired two identical searches
        // for one Undo (live finding #18) — the first of which raced the write it
        // was reacting to.
        if (change.confirmed && !change.itemState.dismissed) {
            // A dismissed card is spliced out of `items`/`recommendedItems`, so Undo has nothing left to
            // patch above. Put it back in the slot it left (J-3: a reload re-ranks, and the restored item
            // can land off the page); reload only for an item this page never removed itself.
            const restored = this.dismissedSlots.Restore(change.key, this.items, this.recommendedItems, (i) => ({ ...i, itemState: change.itemState }));
            if (restored) {
                this.items = restored.Items;
                this.recommendedItems = restored.Recommended;
                this.render.Notify();
                return;
            }
            const wasTracked =
                this.items.some((i) => i.contributorID === change.key) || this.recommendedItems.some((i) => i.contributorID === change.key);
            // Only an undone dismissal can bring back an item this page does not hold; a save or pin elsewhere (a Saved-board Undo)
            // never re-runs this module's search (F-11).
            if (!wasTracked && change.undismissed && this.view === 'search') {
                if (this.state.tab === 'recommended') void this.LoadRecommended();
                else void this.Search();
            }
        }
    }
}
